import React, { useContext, useEffect, useState } from 'react';
import Menu from '@material-ui/core/Menu';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import { HopeGlobalContext } from '@mummssoftware/global-context';
import { visitHeaderID } from '@mummssoftware/utils/constants';
import { HopeVisitPromptCard } from '../HOPE/HopeVisitPromptCard';
import { ElectionAddendumPromptCard } from '../ElectionAddendum/ElectionAddendumPromptCard';
import { EA_VISIT_ICON_ID } from '../ElectionAddendum/ElectionAddendumIcon';
import { useElectionAddendumPrompt } from '../ElectionAddendum/useElectionAddendumPrompt';

const useStyles = makeStyles({
  // Transparent shell: each prompt draws its own card.
  menuPaper: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    overflow: 'visible',
  },
  menuList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 0,
    outline: 'none',
  },
  // Same look the HOPE prompt had as the only Menu child (paper + list padding)
  card: {
    padding: '8px 0',
    outline: 'none',
    // ConfirmModal title (ConfirmModalDiv > ModalText)
    '& > div > div:first-child': {
      fontWeight: 'bold',
    },
  },
});

const menuProps = {
  disableAutoFocus: true,
  disableRestoreFocus: true,
  transitionDuration: 0,
  keepMounted: true,
};

/**
 * Visit-start prompts as separate cards in one popup: HOPE milestone on top,
 * Election Addendum below. Each card is answered on its own and drops out of
 * the stack when answered. Render inside the visit dialog header (before
 * SIAModal) so the popup sits above the visit dialog and below the SIA menu,
 * and hides with the dialog when it is minimized.
 */
type VisitPromptStackProps = {
  /** HOPE chip from SIAModal; null until it mounts (or on non-direct-care visits). */
  hopeChip: HTMLElement | null;
};

export const VisitPromptStack = ({ hopeChip }: VisitPromptStackProps) => {
  const classes = useStyles();
  const { displayHopeVisitPrompt, isOtherInProgHopeVisit } = useContext(
    HopeGlobalContext,
  );
  const { open: eaOpen, ...eaPrompt } = useElectionAddendumPrompt();

  const hopeRequested = displayHopeVisitPrompt && !isOtherInProgHopeVisit;

  // HOPE chip lives in SIAModal (direct-care visits only) and is passed in, so
  // the stack re-anchors when it mounts after the HOPE flag (Visit Log reopen).
  // Without it, fall back to the EA chip, then the visit header, so EA has an
  // anchor on any visit.
  const [fallbackAnchor, setFallbackAnchor] = useState<HTMLElement | null>(
    null,
  );

  useEffect(() => {
    if (hopeChip || !eaOpen) {
      setFallbackAnchor(null);
      return;
    }
    setFallbackAnchor(
      document.getElementById(EA_VISIT_ICON_ID) ??
        document.getElementById(visitHeaderID),
    );
  }, [hopeChip, eaOpen]);

  // Same gate as before: the HOPE prompt only shows with its chip mounted.
  const showHope = hopeRequested && !!hopeChip;
  const anchor = hopeChip ?? fallbackAnchor;
  const open = (showHope || eaOpen) && !!anchor;

  return (
    <Menu
      anchorEl={anchor}
      open={open}
      // Cards are not menu items: no autofocus or tabIndex injection. Menu
      // still lines up the first card with the chip, as it did for HOPE alone.
      variant="menu"
      autoFocus={false}
      classes={{ paper: classes.menuPaper, list: classes.menuList }}
      {...menuProps}
    >
      {showHope ? (
        <Paper elevation={8} className={classes.card}>
          <HopeVisitPromptCard />
        </Paper>
      ) : null}
      {eaOpen ? (
        <Paper elevation={8} className={classes.card}>
          <ElectionAddendumPromptCard {...eaPrompt} />
        </Paper>
      ) : null}
    </Menu>
  );
};

export default VisitPromptStack;
